import React, { useState, useEffect, useRef, useMemo } from "react";
import momentTimezone from "moment-timezone";
import styles from "./EventsUpdate.module.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  UPDATE_EVENT,
  ADD_EVENT,
  UPDATE_EVENT_STATUS,
  UPDATE_EVENT_STATUS_ONLY,
  GET_ALL_LEAGUES,
  SEARCH_VENUES,
  ADD_EVENT_CATEGORY,
  INSERT_CONCERT_ARTIST_CATEGORY,
} from "./api/EventsUpdateQuery";
import { config } from "../../config/config";

import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Calendar, X } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useQuery } from "@apollo/client";
import { getEventStatusDetails, getEventSyncPayload } from "./utils/eventStatus";
import { renderEventStatusBadge } from "../ManageTickets/eventStatusBadge";
import {
  formatDbEventDate,
  formatDbEventTime,
  formatDbEventDateYmd,
  resolveDbEventLocalParts,
} from "../../utils/eventDbDateTime";
import {
  buildConcertCategoryInputs,
  buildManualEventCategoryInputs,
  resolveConcertArtistName,
  resolveConcertPerformerCategory,
  resolveLeagueCategoryId,
} from "./utils/manualEventCategory";
import { getManualEventLeagueCategory } from "./api/getManualEventLeagueCategory";
import { buildTimezoneOptions, TimezoneOption } from "./timezoneOptions";
import {
  buildEventUpdatePayload,
  getEventUpdateValidationError,
  isTbdEvent,
} from "./utils/eventUpdateValidation";

interface Event {
  id: string;
  eventName: string;
  displayName: string;
  venue: string;
  date: string;
  time: string;
  league: string;
  status: boolean;
  rawStatus: string;
  statusLabel?: string;
  isRemoved?: boolean;
  rawDate?: string;
  rawTime?: string;
  timeZone?: string;
  rawDateTimeZone?: string;
}

/** Parse admin date+time in venue TZ → UTC instant for e_date (timestamptz). */
const buildEventDateUtcValue = (date: string, time: string, timeZone?: string | null) => {
  if (!date || !time) return null;
  const zone = timeZone || "America/Chicago";
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  const local = momentTimezone.tz(
    `${date} ${normalizedTime}`,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    zone,
  );
  if (!local.isValid()) return null;
  return local.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

/** Venue-local ISO string for e_date_time_zone text (keeps admin calendar day). */
const buildEventDateTimeZoneValue = (date: string, time: string, timeZone?: string | null) => {
  if (!date || !time) return null;
  const zone = timeZone || "America/Chicago";
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  const local = momentTimezone.tz(
    `${date} ${normalizedTime}`,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    zone,
  );
  if (!local.isValid()) return null;
  return local.format("YYYY-MM-DDTHH:mm:ssZ");
};

const getVenueLocalDateYmd = (eDate?: string | null, timeZone?: string | null, eTime?: string | null, eDateTimeZone?: string | null) =>
  formatDbEventDateYmd({
    e_date: eDate,
    e_time_zone: timeZone,
    e_time: eTime,
    e_date_time_zone: eDateTimeZone,
  });

const EVENT_STATUS_FILTER_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "unnecessary", label: "Unnecessary" },
  { value: "onsale", label: "On Sale" },
  { value: "offsale", label: "Off Sale" },
  { value: "scheduled", label: "Scheduled" },
  { value: "postponed", label: "Postponed" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "date-tbd", label: "Date TBD" },
  { value: "tbd", label: "TBD" },
  { value: "Removal", label: "Removal" },
  { value: "Hidden", label: "Hidden" },
];

const EMPTY_EVENT_LIST_FILTERS = {
  dateFrom: "",
  dateTo: "",
  leagueId: "",
  tbdOnly: false,
  eventStatus: "",
  visibility: "",
};

const getVenueLocalTimeHm = (
  eTime?: string | null,
  eDate?: string | null,
  timeZone?: string | null,
  eDateTimeZone?: string | null,
) => resolveDbEventLocalParts({
  e_date: eDate,
  e_time: eTime,
  e_time_zone: timeZone,
  e_date_time_zone: eDateTimeZone,
})?.timeHm || "";

const EventsUpdate: React.FC = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return document.documentElement.getAttribute("data-theme") === "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const [searchEventName, setSearchEventName] = useState("");
  const [headerSearch, setHeaderSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "hidden" | "tbd">("all");
  const [events, setEvents] = useState<any>({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columnTypes, setColumnTypes] = useState("e_id");
  const [sortType, setSortType] = useState("desc");
  const [showAllDates, setShowAllDates] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customRangeStart, setCustomRangeStart] = useState("");
  const [customRangeEnd, setCustomRangeEnd] = useState("");
  const [showCustomRangeModal, setShowCustomRangeModal] = useState(false);
  const [leagueFilterId, setLeagueFilterId] = useState("");
  const [tbdOnly, setTbdOnly] = useState(false);
  const [eventStatusFilter, setEventStatusFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const listFiltersRef = useRef({ ...EMPTY_EVENT_LIST_FILTERS });
  const eventsRequestRef = useRef(0);
  const skipFilterFetchRef = useRef(true);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const isBulkActionInFlightRef = useRef(false);

  // Actions menu state
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsEventId, setActionsEventId] = useState<string | null>(null);

  // Close event confirm dialog state
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeEventTarget, setCloseEventTarget] = useState<any>(null);
  const [closeEventLoading, setCloseEventLoading] = useState(false);

  const [formData, setFormData] = useState({
    eventName: "",
    displayName: "",
    eventDate: "",
    city: "",
    state: "",
    venue: "",
    eventTime: "",
    timezone: "",
    league: "",
    homeTeam: "",
    awayTeam: "",
  });

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    eventDate: "",
    eventTime: "",
  });

  // Timezone options state
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);
  const [timezoneLoading, setTimezoneLoading] = useState(true);

  const { data: leaguesData, loading: leaguesLoading, refetch: refetchLeagues } = useQuery(GET_ALL_LEAGUES);

  const selectedLeague = useMemo(() => {
    return leaguesData?.leagues?.find((l: any) => l.l_name === formData.league) ?? null;
  }, [leaguesData, formData.league]);

  const normalizeText = (value?: string | null) =>
    (value || "").trim().toLowerCase();

  const selectedLeagueTeamOptions = useMemo<string[]>(() => {
    if (!selectedLeague?.league_categories?.length) return [];

    const leagueName = normalizeText(selectedLeague.l_name);
    const teamNames: Array<string | null | undefined> = selectedLeague.league_categories.flatMap(
      (category: any) => [category.lc_name, category.lc_abbrev, category.lc_market_name]
    );

    return Array.from(
      new Set<string>(
        teamNames
          .filter((name): name is string => !!name && typeof name === "string")
          .map((name: string) => name.trim())
          .filter((name: string) => normalizeText(name) !== leagueName)
      )
    );
  }, [selectedLeague]);

  useEffect(() => {
    if (!selectedLeague) return;

    const validTeamNames = new Set<string>(
      selectedLeagueTeamOptions.map((name) => normalizeText(name))
    );
    const homeTeam = formData.homeTeam?.trim();
    const awayTeam = formData.awayTeam?.trim();

    if (homeTeam && !validTeamNames.has(normalizeText(homeTeam))) {
      setFormData((prev) => ({ ...prev, homeTeam: "" }));
    }
    if (awayTeam && !validTeamNames.has(normalizeText(awayTeam))) {
      setFormData((prev) => ({ ...prev, awayTeam: "" }));
    }
  }, [selectedLeague, selectedLeagueTeamOptions]);

  // Load timezones on mount
  useEffect(() => {
    try {
      const options = buildTimezoneOptions();
      setTimezoneOptions(options);
    } catch (error) {
      console.error('Error loading timezones:', error);
    } finally {
      setTimezoneLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("Leagues data:", leaguesData, "loading:", leaguesLoading);
    if (!leaguesLoading && (!leaguesData?.leagues || leaguesData.leagues.length === 0)) {
      toast.info("No active leagues were returned by the server. Please contact admin.");
    }
  }, [leaguesData, leaguesLoading]);

  const [searchVenues, { data: venuesData, loading: venuesLoading }] = useLazyQuery(SEARCH_VENUES, {
    fetchPolicy: "network-only",
  });

  const themeColors = {
    textPrimary: darkMode ? "#f1f5f9" : "#111827",
    textSecondary: darkMode ? "#94a3b8" : "#666",
    textTertiary: darkMode ? "#64748b" : "#888",
    textLabel: darkMode ? "#f1f5f9" : "#374151",
    errorText: "#dc2626",
    infoText: darkMode ? "#93c5fd" : "#3b82f6",
    bgPrimary: darkMode ? "#1e293b" : "#ffffff",
    bgSecondary: darkMode ? "#334155" : "#f8fafc",
    bgHover: darkMode ? "#475569" : "#f3f4f6",
    bgInfoLight: darkMode ? "#1e293b" : "rgb(221 229 255)",
    borderColor: darkMode ? "#334155" : "#e5e7eb",
    borderInput: darkMode ? "#475569" : "#d1d5db",
    buttonBgPrimary: darkMode ? "#818cf8" : "#0f172a",
    buttonBgHover: darkMode ? "#6366f1" : "#1e293b",
    buttonTextPrimary: "#ffffff",
    infoBoxBg: darkMode ? "#1e293b" : "rgb(221 229 255)",
    infoBoxText: darkMode ? "#93c5fd" : "rgb(29, 78, 216)",
  };

  const [updateEventStatus] = useMutation(UPDATE_EVENT_STATUS, {
    onCompleted: () => {
      if (isBulkActionInFlightRef.current) {
        return;
      }
      fetchEventsFromBackend();
      setSelectedIds([]);
      toast.dismiss();
      toast.success("Event status updated successfully!", { toastId: "event-status-success" });
    },
    onError: (err) => {
      console.error("Error updating event:", err);
      toast.dismiss();
      toast.error("Failed to update event status", { toastId: "event-status-error" });
    },
  });

  // Dedicated close-only mutation; updates only e_status and preserves e_is_active
  const [updateEventStatusOnly] = useMutation(UPDATE_EVENT_STATUS_ONLY);

  const [addEventMutation] = useMutation(ADD_EVENT, {
    onCompleted: () => {
      toast.dismiss();
      toast.success("Event added successfully!");
      setOpenAddModal(false);
      setFormData({
        eventName: "",
        displayName: "",
        eventDate: "",
        city: "",
        state: "",
        venue: "",
        eventTime: "",
        timezone: "",
        league: "",
        homeTeam: "",
        awayTeam: "",
      });
      setAddLoading(false);
      setCurrentPage(1);
      setFilterStatus("all");
      setShowAllDates(true);
      setColumnTypes("e_id");
      setSortType("desc");
      setSearchEventName("");
      setHeaderSearch("");
      fetchEventsFromBackend("%%", 1);
    },
    onError: (err) => {
      console.error("Error adding event:", err);
      toast.dismiss();
      toast.error("Failed to add event. Please try again.");
      setAddLoading(false);
    },
  });

  const [insertConcertArtistCategory] = useMutation(INSERT_CONCERT_ARTIST_CATEGORY);

  const [addEventCategory] = useMutation(ADD_EVENT_CATEGORY, {
    onError: (err) => {
      console.error("Error adding event category:", err);
      toast.error("Event saved but failed to link category. Please contact support.");
    },
    errorPolicy: "all",
  });

  const getGraphQLErrorMessage = (err: unknown, fallback: string) => {
    const graphQLError = (err as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message;
    return graphQLError || (err as Error)?.message || fallback;
  };

  const normalizeEventTimeForSave = (timeValue: string, existingRawTime?: string) => {
    const trimmed = timeValue?.trim();
    if (trimmed) {
      return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
    }
    if (existingRawTime) {
      const existingMatch = existingRawTime.toString().match(/^(\d{2}:\d{2}(?::\d{2})?)/);
      if (existingMatch) {
        return existingMatch[1].length === 5 ? `${existingMatch[1]}:00` : existingMatch[1];
      }
    }
    return null;
  };

  const buildManualEventInsertInput = ({
    normalizedEventName,
    normalizedVenue,
    eventBrandName,
    eventDateUtc,
    timeForSave,
    timezoneValue,
    formattedDateTimeZone,
    leagueName,
  }: {
    normalizedEventName: string;
    normalizedVenue: string;
    eventBrandName: string;
    eventDateUtc: string | null;
    timeForSave: string;
    timezoneValue: string;
    formattedDateTimeZone: string | null;
    leagueName: string;
  }) => ({
    e_name: normalizedEventName,
    e_date: eventDateUtc,
    e_time: timeForSave,
    e_city: formData.city.trim(),
    e_state: formData.state.trim(),
    e_stadium_name: normalizedVenue,
    e_address: normalizedVenue,
    e_brand_name: eventBrandName,
    e_platform_name: "ticketmaster",
    e_game_id: null,
    e_time_zone: timezoneValue,
    e_date_time_zone: formattedDateTimeZone,
    e_game_type: /^NCAA/i.test(leagueName) ? leagueName : null,
    e_country: null,
    e_latitude: null,
    e_longitude: null,
    e_is_active: true,
    e_status: "Active",
  });

  const formatEventDateTimeZoneValue = buildEventDateTimeZoneValue;

  const [updateEvent] = useMutation(UPDATE_EVENT, {
    onCompleted: () => {
      toast.dismiss();
      toast.success("Event updated successfully!", {
        toastId: "event-update-success",
      });
      setOpenEditModal(false);
      setEditLoading(false);
      fetchEventsFromBackend();
    },
    onError: (err) => {
      console.error("Update error:", err);
      toast.dismiss();
      toast.error(getGraphQLErrorMessage(err, "Failed to update event"));
      setEditLoading(false);
    },
  });

  const handlePageChange = (_event: any, newPage: number) => {
    setCurrentPage(newPage);
    setSelectedIds([]);
  };

  const resetServerListFilters = () => {
    listFiltersRef.current = { ...EMPTY_EVENT_LIST_FILTERS };
    setDateFrom("");
    setDateTo("");
    setLeagueFilterId("");
    setTbdOnly(false);
    setEventStatusFilter("");
  };

  const handleClearAllSearches = () => {
    setHeaderSearch("");
    setSearchEventName("");
    setFilterStatus("all");
    setShowAllDates(true);
    setColumnTypes("e_id");
    setSortType("desc");
    setCurrentPage(1);
    setSelectedIds([]);
    resetServerListFilters();
    fetchEventsFromBackend("%%", 1);
  };

  const filterEventsByDate = (eventsData: any) => {
    if (!eventsData || !eventsData.events) return eventsData;

    let filteredEvents = eventsData.events;
    let filteredAggregate = eventsData.events_aggregate;

    if (!showAllDates) {
      filteredEvents = eventsData.events.filter((event: any) => {
        const hasValidDate = event.e_date !== null && event.e_date !== undefined && event.e_date !== "";
        const isTBD = event.e_is_active?.toLowerCase().includes("tbd");
        const isValid = hasValidDate && !isTBD;
        return isValid;
      });
      filteredAggregate = {
        ...eventsData.events_aggregate,
        aggregate: {
          ...eventsData.events_aggregate?.aggregate,
          count: filteredEvents.length,
        },
      };
    }

    return {
      ...eventsData,
      events: filteredEvents,
      events_aggregate: filteredAggregate,
    };
  };

  const fetchEventsFromBackend = (searchTextOverride?: string, pageOverride?: number) => {
    setLoading(true);
    const requestId = eventsRequestRef.current + 1;
    eventsRequestRef.current = requestId;
    const searchText = searchTextOverride !== undefined
      ? (searchTextOverride === "%%" ? "" : searchTextOverride)
      : searchEventName;
    const page = pageOverride ?? currentPage;
    const offset = (page - 1) * rowsPerPage;
    const filters = listFiltersRef.current;
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || "").replace(/\/+$/, "");
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
    const hasuraRole = localStorage.getItem("x-hasura-user-role");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers["x-hasura-role"] = hasuraRole;

    const applyEventsResult = (data: any) => {
      if (requestId !== eventsRequestRef.current) return;
      if (data?.events && Array.isArray(data.events)) {
        const filteredData = filterEventsByDate(data);
        setEvents(filteredData);
        if (filteredData?.events_aggregate?.aggregate?.count !== undefined) {
          setTotalCount(filteredData.events_aggregate.aggregate.count);
        }
      } else {
        setEvents({ events: [], events_aggregate: { aggregate: { count: 0 } } });
        setTotalCount(0);
      }
      setLoading(false);
    };

    if (!nodeApiBaseUrl) {
      applyEventsResult(null);
      return;
    }

    fetch(`${nodeApiBaseUrl}/api/filterEventsUpdate`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        searchText,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        leagueId: filters.leagueId,
        tbdOnly: filters.tbdOnly,
        eventStatus: filters.eventStatus,
        visibility: filters.visibility,
        limit: rowsPerPage,
        offset,
        orderBy: { column: "e_id", direction: "desc" },
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.message || "Failed to load events");
        applyEventsResult(data);
      })
      .catch((err) => {
        console.error("filterEventsUpdate error:", err);
        applyEventsResult(null);
      });
  };

  useEffect(() => {
    console.log("Component mounted - triggering initial fetch");
    fetchEventsFromBackend();
  }, []);

  useEffect(() => {
    if (currentPage > 1 || searchEventName || !showAllDates) {
      console.log("Dependencies changed - refetching");
      // Typing waits a moment so each keystroke doesn't fire its own search.
      const timer = window.setTimeout(() => fetchEventsFromBackend(), searchEventName ? 300 : 0);
      return () => window.clearTimeout(timer);
    }
  }, [searchEventName, currentPage, rowsPerPage, showAllDates]);

  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false;
      return;
    }
    fetchEventsFromBackend(undefined, 1);
  }, [dateFrom, dateTo, leagueFilterId, tbdOnly, eventStatusFilter, visibilityFilter]);

  const handleRefresh = () => {
    setCurrentPage(1);
    setFilterStatus("all");
    setShowAllDates(true);
    setSearchEventName("");
    setHeaderSearch("");
    setSelectedIds([]);
    resetServerListFilters();
    fetchEventsFromBackend("%%", 1);
  };

  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !isNaN(d.getTime());
  };

  const isValidTime = (timeStr: string) => {
    if (!timeStr) return false;
    const d = new Date(`1970-01-01T${timeStr}`);
    return !isNaN(d.getTime());
  };

  const formatEventDateDisplay = (event: {
    e_date?: string | null;
    e_time?: string | null;
    e_time_zone?: string | null;
    e_date_time_zone?: string | null;
  }) => {
    const formatted = formatDbEventDate({
      e_date: event.e_date,
      e_time: event.e_time,
      e_time_zone: event.e_time_zone,
      e_date_time_zone: event.e_date_time_zone,
    }, 'short');
    return formatted === 'TBD' ? 'TBD' : formatted;
  };

  const normalizeLeagueValue = (value?: string) => {
    const normalized = value?.trim() || "";
    if (!normalized) return "";
    return normalized.toLowerCase().includes("ticketmaster") ? "" : normalized;
  };

  // Theatre & Concerts leagues ki team section hide cheyyali
  const shouldHideTeamSection = (leagueName?: string) => {
    const normalized = (leagueName || "").trim().toLowerCase();
    return normalized === "theatre" || normalized === "concerts" || normalized === "concert";
  };

  const getEventLeagueName = (event: any) => {
    const leagueName = normalizeLeagueValue(event?.event_categories?.[0]?.league_category?.league?.l_name);
    if (leagueName) return leagueName;

    const fallbackLeague = normalizeLeagueValue(event?.e_state);
    if (fallbackLeague) return fallbackLeague;

    return "";
  };

  const getLeagueBadgeClass = (league: string) => {
    const leagueUpper = league?.toUpperCase() || "";
    if (leagueUpper === "NBA") return styles.leagueNBA;
    if (leagueUpper === "NFL") return styles.leagueNFL;
    if (leagueUpper === "MLB") return styles.leagueMLB;
    if (leagueUpper === "NHL") return styles.leagueNHL;
    if (leagueUpper === "MLS") return styles.leagueMLS;
    if (leagueUpper === "WNBA") return styles.leagueWNBA;
    if (leagueUpper === "NCAAM" || leagueUpper === "NCAAW" || leagueUpper === "NCAAFB") return styles.leagueNCAA;
    if (leagueUpper === "CONCERT") return styles.leagueConcert;
    return styles.leagueDefault;
  };

  const transformedEvents: Event[] = (events?.events || []).map((event: any) => {
    const statusDetails = getEventStatusDetails(event);
    return {
      id: event.e_id,
      eventName: event.e_name || "",
      displayName: event.e_brand_name || event.e_name || "",
      venue: event.e_address || event.e_city || "N/A",
      date: formatEventDateDisplay(event),
      rawDate: event.e_date || "",
      timeZone: event.e_time_zone || "",
      time: formatDbEventTime({
        e_date: event.e_date,
        e_time: event.e_time,
        e_time_zone: event.e_time_zone,
        e_date_time_zone: event.e_date_time_zone,
      }, 'short') || "TBD",
      rawTime: event.e_time || "",
      rawDateTimeZone: event.e_date_time_zone || "",
      league: getEventLeagueName(event),
      status: statusDetails.isActive,
      rawStatus: event.e_status || "",
      statusLabel: statusDetails.label,
      isRemoved: statusDetails.isRemoved,
    };
  });

  const selectedEvents = transformedEvents.filter((event: any) => selectedIds.includes(event.id));
  const selectedActiveCount = selectedEvents.filter((event: any) => event.status === true && !event.isRemoved).length;
  const selectedHiddenCount = selectedEvents.filter((event: any) => event.status === false || event.isRemoved).length;
  const selectedHasActive = selectedActiveCount > 0;
  const selectedHasHidden = selectedHiddenCount > 0;
  const isMixedStatusSelection = selectedHasActive && selectedHasHidden;
  const hideButtonDisabled = bulkActionLoading || selectedIds.length === 0 || !selectedHasActive || isMixedStatusSelection;
  const showButtonDisabled = bulkActionLoading || selectedIds.length === 0 || !selectedHasHidden || isMixedStatusSelection;

  const filteredEvents = transformedEvents.filter((event: any) => {
    const matchesHeaderSearch =
      headerSearch === "" ||
      event.eventName?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.venue?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.league?.toLowerCase().includes(headerSearch.toLowerCase()) ||
      event.displayName?.toLowerCase().includes(headerSearch.toLowerCase());

    let matchesStatus = true;
    if (filterStatus === "active") {
      matchesStatus = event.status === true;
    } else if (filterStatus === "hidden") {
      matchesStatus = event.status === false;
    } else if (filterStatus === "tbd") {
      matchesStatus = event.date === "TBD" && event.time === "TBD";
    }

    return matchesHeaderSearch && matchesStatus;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredEvents.map((e) => e.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((pid) => pid !== id));
    }
  };

  const isAllSelected = filteredEvents.length > 0 && filteredEvents.every((e) => selectedIds.includes(e.id));

  const handleColumnSort = (column: string) => {
    if (columnTypes === column) {
      setSortType((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setColumnTypes(column);
      setSortType("asc");
    }
  };

  const renderSortHeader = (label: string, column: string) => {
    const isSorted = columnTypes === column;
    return (
      <th>
        <button type="button" className={styles.sortHeader} onClick={() => handleColumnSort(column)}>
          <span>{label}</span>
          <span className={styles.sortArrows} aria-hidden="true">
            <span className={`${styles.sortArrow} ${isSorted && sortType !== "asc" ? styles.sortArrowIdle : ""}`}>▲</span>
            <span className={`${styles.sortArrow} ${isSorted && sortType !== "desc" ? styles.sortArrowIdle : ""}`}>▼</span>
          </span>
        </button>
      </th>
    );
  };

  const handleBulkHide = async () => {
    if (selectedIds.length === 0 || isMixedStatusSelection) {
      if (isMixedStatusSelection) {
        toast.info("Selected events contain different statuses. Please select only Active events to hide.");
      }
      return;
    }

    const selectedIdsSnapshot = [...selectedIds];
    isBulkActionInFlightRef.current = true;
    setBulkActionLoading(true);

    try {
      for (const id of selectedIdsSnapshot) {
        await updateEventStatus({
          variables: { e_id: id, ...getEventSyncPayload(false) },
        });
      }

      fetchEventsFromBackend();
      setSelectedIds([]);
      toast.dismiss();
      toast.success(`Successfully hid ${selectedIdsSnapshot.length} event(s)`, { toastId: "bulk-hide-success" });
    } catch (err) {
      toast.dismiss();
      toast.error("Bulk hide failed. Please try again.", { toastId: "bulk-hide-error" });
    } finally {
      isBulkActionInFlightRef.current = false;
      setBulkActionLoading(false);
    }
  };

  const handleBulkShow = async () => {
    if (selectedIds.length === 0 || isMixedStatusSelection) {
      if (isMixedStatusSelection) {
        toast.info("Selected events contain different statuses. Please select only Hidden events to show.");
      }
      return;
    }

    const selectedIdsSnapshot = [...selectedIds];
    isBulkActionInFlightRef.current = true;
    setBulkActionLoading(true);

    try {
      for (const id of selectedIdsSnapshot) {
        await updateEventStatus({
          variables: { e_id: id, ...getEventSyncPayload(true) },
        });
      }

      fetchEventsFromBackend();
      setSelectedIds([]);
      toast.dismiss();
      toast.success(`Successfully showed ${selectedIdsSnapshot.length} event(s)`, { toastId: "bulk-show-success" });
    } catch (err) {
      toast.dismiss();
      toast.error("Bulk show failed. Please try again.", { toastId: "bulk-show-error" });
    } finally {
      isBulkActionInFlightRef.current = false;
      setBulkActionLoading(false);
    }
  };

  const handleHide = (id: string) => {
    updateEventStatus({
      variables: { e_id: id, ...getEventSyncPayload(false) },
    });
  };

  const handleShow = (id: string) => {
    updateEventStatus({
      variables: { e_id: id, ...getEventSyncPayload(true) },
    });
  };

  const todayDate = new Date().toISOString().split("T")[0];
  const currentTime = new Date().toTimeString().slice(0, 5);

  const handleAddEvent = async () => {
    if (
      !formData.eventName.trim() ||
      !formData.eventDate ||
      !formData.city.trim() ||
      !formData.state.trim() ||
      !formData.venue.trim() ||
      !formData.eventTime ||
      !formData.league.trim()
    ) {
      toast.dismiss();
      toast.warning("Please fill all required fields");
      return;
    }

    const normalizedEventName = formData.eventName.trim();
    const normalizedVenue = formData.venue.trim();
    const normalizedDate = formData.eventDate;

    const isDuplicate = (events?.events || []).some((event: any) => {
      const sameName = String(event.e_name || "").trim().toLowerCase() === normalizedEventName.toLowerCase();
      const sameVenue = String(event.e_address || "").trim().toLowerCase() === normalizedVenue.toLowerCase();
      const sameDate = getVenueLocalDateYmd(event.e_date, event.e_time_zone) === normalizedDate;
      return sameName && sameVenue && sameDate;
    });

    if (isDuplicate) {
      toast.dismiss();
      toast.warning("An event with the same name, date, and venue already exists.");
      return;
    }

    const selectedLeague = leaguesData?.leagues?.find((l: any) => l.l_name === formData.league);
    const leagueCategories = selectedLeague?.league_categories || [];
    let leagueCategoryId = resolveLeagueCategoryId(formData.league, leagueCategories);
    const hasManualTeamNames = Boolean(formData.homeTeam?.trim() || formData.awayTeam?.trim());
    const isTheatreOrConcert = shouldHideTeamSection(formData.league);

    if (!leagueCategoryId && !hasManualTeamNames && !isTheatreOrConcert) {
      toast.error("Selected league does not have a category mapping. Please contact admin.");
      return;
    }

    setAddLoading(true);

    try {
      if (isTheatreOrConcert) {
        const leagueCategory = await getManualEventLeagueCategory(formData.league.trim());
        leagueCategoryId = leagueCategory.lc_id;
      }

      const eventBrandName = formData.displayName.trim() || formData.eventName.trim();
      const timeForSave = normalizeEventTimeForSave(formData.eventTime) || formData.eventTime;
      const timezoneValue = formData.timezone?.trim() || "America/Chicago";
      const eventDateUtc = buildEventDateUtcValue(
        normalizedDate,
        timeForSave,
        timezoneValue,
      );
      const formattedDateTimeZone = buildEventDateTimeZoneValue(
        normalizedDate,
        timeForSave,
        timezoneValue,
      );

      if (!eventDateUtc) {
        toast.warning("Enter a valid event date and time");
        setAddLoading(false);
        return;
      }

      const { data: eventData } = await addEventMutation({
        variables: {
          eventInput: buildManualEventInsertInput({
            normalizedEventName,
            normalizedVenue,
            eventBrandName,
            eventDateUtc,
            timeForSave,
            timezoneValue,
            formattedDateTimeZone,
            leagueName: formData.league.trim(),
          }),
        },
      });

      const newEventId = eventData.insert_events_one.e_id;

      const performerCategory = isTheatreOrConcert
        ? resolveConcertPerformerCategory(
            normalizedEventName,
            formData.displayName,
            formData.league,
            leagueCategories,
          )
        : undefined;
      let performerCategoryId = performerCategory?.lc_id;

      if (isTheatreOrConcert && !performerCategoryId) {
        const artistName = resolveConcertArtistName(
          normalizedEventName,
          formData.displayName,
          formData.league,
        );
        if (!selectedLeague?.l_id || !artistName) {
          toast.error("Event saved, but the artist category could not be created.");
          setAddLoading(false);
          return;
        }

        const { data: createdCategory } = await insertConcertArtistCategory({
          variables: {
            l_id: selectedLeague.l_id,
            lc_name: artistName,
            lc_ticketmaster_name: artistName,
            lc_category_ref_id: crypto.randomUUID(),
          },
        });
        performerCategoryId = createdCategory?.insert_league_categories_one?.lc_id;
        if (!performerCategoryId) {
          toast.error("Event saved, but the artist category could not be created.");
          setAddLoading(false);
          return;
        }
        refetchLeagues().catch(() => undefined);
      }

      const manualCategoryInputs = isTheatreOrConcert
        ? buildConcertCategoryInputs(leagueCategoryId, performerCategoryId)
        : buildManualEventCategoryInputs({
          leagueCategoryId,
          leagueCategories,
          homeTeamName: formData.homeTeam,
          awayTeamName: formData.awayTeam,
        });

      if (manualCategoryInputs.length === 0) {
        toast.dismiss();
        toast.error("Event saved but no matching league category was found. Please verify the selected league and teams.");
        setAddLoading(false);
        return;
      }

      console.log("Adding event categories: newEventId", newEventId, "categoryInputs", manualCategoryInputs, "selectedLeague", selectedLeague);
      try {
        const categoryResults = await Promise.all(
          manualCategoryInputs.map((categoryInput) =>
            addEventCategory({
              variables: {
                eventCategoryInput: {
                  e_id: newEventId,
                  lc_id: categoryInput.lc_id,
                  ec_is_home: categoryInput.ec_is_home,
                },
              },
            })
          )
        );

        const linkedCategoryCount = categoryResults.filter(
          (result) => result?.data?.insert_event_categories_one
        ).length;

        if (linkedCategoryCount === 0) {
          toast.error("Event saved but failed to link category. Please contact support.");
        } else {
          toast.success("Event added successfully!");
        }
      } catch (catErr) {
        console.error("addEventCategory error:", catErr);
        toast.error("Event saved but failed to link category. Please contact support.");
      }
      setOpenAddModal(false);
      setFormData({
        eventName: "",
        displayName: "",
        eventDate: "",
        city: "",
        state: "",
        venue: "",
        eventTime: "",
        timezone: "",
        league: "",
        homeTeam: "",
        awayTeam: "",
      });
      setAddLoading(false);
      setCurrentPage(1);
      setFilterStatus("all");
      setShowAllDates(true);
      setColumnTypes("e_id");
      setSortType("desc");
      setSearchEventName("");
      setHeaderSearch("");
      fetchEventsFromBackend("%%", 1);
    } catch (err) {
      console.error("Error adding event:", err);
      toast.dismiss();
      toast.error(getGraphQLErrorMessage(err, "Failed to add event. Please try again."));
      setAddLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setFormData({
      eventName: "",
      displayName: "",
      eventDate: "",
      city: "",
      state: "",
      venue: "",
      eventTime: "",
      timezone: "",
      league: "",
      homeTeam: "",
      awayTeam: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedEvent) return;

    const selectedEventIsTbd = isTbdEvent(selectedEvent);

    const normalizedTime = normalizeEventTimeForSave(editFormData.eventTime, selectedEvent.rawTime);
    const timeForSave = normalizedTime || selectedEvent.rawTime || "";
    const eventDateUtc = buildEventDateUtcValue(
      editFormData.eventDate,
      timeForSave,
      selectedEvent.timeZone,
    );
    const formattedDateTimeZone = formatEventDateTimeZoneValue(
      editFormData.eventDate,
      timeForSave,
      selectedEvent.timeZone,
    );

    const validationError = getEventUpdateValidationError(
      editFormData,
      selectedEventIsTbd,
      eventDateUtc,
    );
    if (validationError) {
      toast.warning(validationError);
      return;
    }

    setEditLoading(true);

    try {
      await updateEvent({
        variables: {
          e_id: selectedEvent.id,
          eventData: buildEventUpdatePayload({
            formData: editFormData,
            isTbd: selectedEventIsTbd,
            eventDateUtc,
            normalizedTime,
            formattedDateTimeZone,
            timeZone: selectedEvent.timeZone,
          }),
        },
      });
    } catch (error) {
      console.error("Update Error:", error);
      toast.dismiss();
      toast.error(getGraphQLErrorMessage(error, "Failed to update event"));
      setEditLoading(false);
    }
  };

  const handleEdit = (event: any) => {
    console.log(event);
    setSelectedEvent(event);

    const eventDate =
      getVenueLocalDateYmd(event.rawDate, event.timeZone, event.rawTime, event.rawDateTimeZone);
    const eventTime = getVenueLocalTimeHm(
      event.rawTime,
      event.rawDate,
      event.timeZone,
      event.rawDateTimeZone,
    );

    setEditFormData({
      displayName: event.displayName,
      eventDate,
      eventTime,
    });

    setOpenEditModal(true);
  };

  const handleActionMenuOpen = (anchorEl: HTMLElement, event: any) => {
    setActionsAnchorEl(anchorEl);
    setActionsEventId(event.id);
    setCloseEventTarget(event);
  };

  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
    setActionsEventId(null);
  };

  const openCloseConfirmDialog = () => {
    const row = transformedEvents.find((item: any) => item.id === actionsEventId) || closeEventTarget;
    if (!row) return;
    setCloseEventTarget(row);
    setCloseConfirmOpen(true);
    closeActionsMenu();
  };

  const handleConfirmCloseEvent = async () => {
    if (!closeEventTarget?.id) return;

    setCloseEventLoading(true);
    try {
      await updateEventStatusOnly({
        variables: {
          e_id: closeEventTarget.id,
          e_status: "closed",
        },
      });

      toast.dismiss();
      toast.success("Event closed successfully", { toastId: "close-event-success" });
      setCloseConfirmOpen(false);
      setCloseEventTarget(null);
      fetchEventsFromBackend();
    } catch (err) {
      console.error("Close event error:", err);
      toast.dismiss();
      toast.error("Failed to close event", { toastId: "close-event-error" });
    } finally {
      setCloseEventLoading(false);
    }
  };

  const compareLoadedText = (left: string, right: string) =>
    left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });

  const loadedTimeSortKey = (timeLabel: string) => {
    const match = String(timeLabel || "").trim().match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (!match) return "";
    let hours = Number(match[1]) % 12;
    if (match[3].toUpperCase() === "PM") hours += 12;
    return `${String(hours).padStart(2, "0")}:${match[2]}`;
  };

  const compareLoadedEvents = (left: any, right: any) => {
    if (columnTypes === "e_id") return 0;
    const direction = sortType === "asc" ? 1 : -1;
    const byId = String(left.id).localeCompare(String(right.id));
    const withMissingLast = (leftKey: string, rightKey: string) => {
      if (!leftKey || !rightKey) {
        if (!leftKey && !rightKey) return byId;
        return !leftKey ? 1 : -1;
      }
      const compared = leftKey.localeCompare(rightKey);
      return compared !== 0 ? compared * direction : byId;
    };

    if (columnTypes === "e_name") {
      const compared = compareLoadedText(left.eventName || "", right.eventName || "");
      return compared !== 0 ? compared * direction : byId;
    }
    if (columnTypes === "e_date") {
      return withMissingLast(left.rawDate ? String(left.rawDate) : "", right.rawDate ? String(right.rawDate) : "");
    }
    if (columnTypes === "e_time") {
      return withMissingLast(loadedTimeSortKey(left.time), loadedTimeSortKey(right.time));
    }
    if (columnTypes === "league") {
      const compared = compareLoadedText(left.league || "Unknown", right.league || "Unknown");
      return compared !== 0 ? compared * direction : byId;
    }
    if (columnTypes === "e_status") {
      const compared = compareLoadedText(left.rawStatus || "", right.rawStatus || "");
      return compared !== 0 ? compared * direction : byId;
    }
    return 0;
  };

  const sortedEvents = [...filteredEvents].sort(compareLoadedEvents);
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const leagueFilterOptions = [
    { l_id: "", l_name: "All leagues" },
    ...((leaguesData?.leagues || []) as { l_id: string; l_name: string }[]),
  ];

  return (
    <div className={`${styles.container} ${darkMode ? styles.darkMode : ""}`}>
      <div className={styles.header}>
        <div className={styles.searchSection}>
          <div>
            <p className={styles.subtitle}>Manage Event Dates & Times</p>
            <h1 className={styles.title}>Events Update</h1>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <div className={styles.iconGroup}>
            <button className={styles.iconBtn} onClick={handleRefresh} disabled={loading}>
              <RefreshRoundedIcon style={{ opacity: loading ? 0.5 : 1 }} />
            </button>

            <button className={styles.iconBtn} onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </button>
          </div>
          <button className={styles.addBtn} onClick={() => setOpenAddModal(true)}>
            + Add Event
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.filterRow}>
          <div className={styles.searchWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by event name..."
              value={searchEventName}
              onChange={(e) => {
                setSearchEventName(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchEventName && (
              <button
                type="button"
                onClick={handleClearAllSearches}
                className={styles.clearSearchBtn}
              >
                ✕
              </button>
            )}
          </div>

          <div className={styles.dateFilterWrapper}>
            <button
              type="button"
              className={styles.dateFilterBtn}
              onClick={() => {
                setCustomRangeStart(dateFrom);
                setCustomRangeEnd(dateTo);
                setShowCustomRangeModal(true);
              }}
            >
              <Calendar size={14} style={{ marginRight: 4 }} />
              Custom
            </button>
            {(dateFrom || dateTo) && (
              <span className={styles.activeDatePill}>
                {dateFrom && dateTo && dateFrom !== dateTo ? `${dateFrom} → ${dateTo}` : (dateFrom || dateTo)}
                <X
                  size={14}
                  style={{ marginLeft: 6, cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    listFiltersRef.current = { ...listFiltersRef.current, dateFrom: "", dateTo: "" };
                    setDateFrom("");
                    setDateTo("");
                    setCustomRangeStart("");
                    setCustomRangeEnd("");
                    setCurrentPage(1);
                  }}
                />
              </span>
            )}
          </div>
          {showCustomRangeModal && (
            <div className={styles.modalOverlay}>
              <div className={`${styles.modalContainer} ${styles.customRangeModal}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <h2><Calendar size={16} style={{ marginRight: 8 }} /> Select Date Range</h2>
                  <button type="button" className={styles.modalClose} onClick={() => setShowCustomRangeModal(false)}>
                    <X size={18} />
                  </button>
                </div>
                <div className={styles.modalBody}>
                  <div className={styles.formGroup}>
                    <label>Start Date</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={customRangeStart}
                      onChange={(e) => setCustomRangeStart(e.target.value)}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>End Date</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={customRangeEnd}
                      onChange={(e) => setCustomRangeEnd(e.target.value)}
                    />
                  </div>
                </div>
                <div className={styles.modalFooter}>
                  <button type="button" className={styles.customRangeCancel} onClick={() => setShowCustomRangeModal(false)}>Cancel</button>
                  <button
                    type="button"
                    className={styles.customRangeApply}
                    onClick={() => {
                      if (!customRangeStart || !customRangeEnd) return;
                      listFiltersRef.current = {
                        ...listFiltersRef.current,
                        dateFrom: customRangeStart,
                        dateTo: customRangeEnd,
                      };
                      setDateFrom(customRangeStart);
                      setDateTo(customRangeEnd);
                      setCurrentPage(1);
                      setShowCustomRangeModal(false);
                    }}
                  >
                    Apply Range
                  </button>
                </div>
              </div>
            </div>
          )}
          <label className={styles.filterField}>
            League
            <Autocomplete
              className={styles.leagueFilterAutocomplete}
              size="small"
              options={leagueFilterOptions}
              value={
                leagueFilterOptions.find((league) => league.l_id === leagueFilterId) || leagueFilterOptions[0]
              }
              onChange={(_event, newValue) => {
                const nextLeagueId = newValue?.l_id || "";
                listFiltersRef.current = { ...listFiltersRef.current, leagueId: nextLeagueId };
                setLeagueFilterId(nextLeagueId);
                setCurrentPage(1);
              }}
              getOptionLabel={(option) => option?.l_name || ""}
              isOptionEqualToValue={(option, value) => option.l_id === value.l_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search leagues"
                />
              )}
            />
          </label>

          <div className={styles.filterTabs}>
            <button
              className={`${styles.filtersBtn} ${filterStatus === "tbd" ? styles.otherFilter + " " + styles.active : ""
                }`}
              onClick={() => {
                listFiltersRef.current = { ...listFiltersRef.current, tbdOnly: true, visibility: "" };
                setTbdOnly(true);
                setVisibilityFilter("");
                setFilterStatus("tbd");
                setCurrentPage(1);
              }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
              Show TBD Only
            </button>
            <div className={styles.filterGroup}>
              <button
                className={`${styles.filterBtn} ${filterStatus === "active" ? styles.activeFilter : ""}`}
                onClick={() => {
                  listFiltersRef.current = { ...listFiltersRef.current, tbdOnly: false, visibility: "show" };
                  setTbdOnly(false);
                  setVisibilityFilter("show");
                  setFilterStatus("active");
                  setCurrentPage(1);
                }}
              >
                Active
              </button>

              <button
                className={`${styles.filterBtn} ${filterStatus === "hidden" ? styles.hiddenFilter : ""}`}
                onClick={() => {
                  listFiltersRef.current = { ...listFiltersRef.current, tbdOnly: false, visibility: "hidden" };
                  setTbdOnly(false);
                  setVisibilityFilter("hidden");
                  setFilterStatus("hidden");
                  setCurrentPage(1);
                }}
              >
                Hidden
              </button>

              <button
                className={`${styles.filterBtn} ${filterStatus === "all" ? styles.allFilter + " " + styles.active : ""}`}
                onClick={() => {
                  listFiltersRef.current = { ...listFiltersRef.current, tbdOnly: false, visibility: "" };
                  setTbdOnly(false);
                  setVisibilityFilter("");
                  setFilterStatus("all");
                  setCurrentPage(1);
                }}
              >
                All
              </button>
            </div>
            <label className={styles.filterField}>
              Event Status
              <select
                className={styles.filterControl}
                value={eventStatusFilter}
                onChange={(e) => {
                  listFiltersRef.current = { ...listFiltersRef.current, eventStatus: e.target.value };
                  setEventStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                {EVENT_STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className={styles.tableCard}>
        {selectedIds.length > 0 && (
          <div className={styles.bulkSelectionBar}>
            <div className={styles.bulkSelectionText}>
              <span>
                {selectedIds.length} event{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              <span>
                {isMixedStatusSelection
                  ? "Mixed statuses selected — use only one type at a time."
                  : selectedHasActive
                    ? "Ready to hide active event(s)."
                    : selectedHasHidden
                      ? "Ready to show hidden event(s)."
                      : "Select events to perform bulk actions."}
              </span>
            </div>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkHide}
              disabled={hideButtonDisabled}
              startIcon={<VisibilityOffOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderColor: "#dc2626",
                color: "#dc2626",
                borderRadius: "10px",
                fontWeight: 600,
                minWidth: "148px",
                "&:hover": { borderColor: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" },
              }}
            >
              Hide Selected
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkShow}
              disabled={showButtonDisabled}
              startIcon={<VisibilityOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderColor: "#16a34a",
                color: "#16a34a",
                borderRadius: "10px",
                fontWeight: 600,
                minWidth: "148px",
                "&:hover": { borderColor: "#15803d", backgroundColor: "rgba(22,163,74,0.08)" },
              }}
            >
              Show Selected
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedIds([])}
              disabled={bulkActionLoading}
              sx={{
                textTransform: "none",
                color: themeColors.textSecondary,
                minWidth: "116px",
              }}
            >
              Clear Selection
            </Button>
            {bulkActionLoading && <CircularProgress size={20} />}
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress />
          </div>
        ) : sortedEvents.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>No Events Found</h2>
            <p>
              {headerSearch || searchEventName || dateFrom || dateTo || leagueFilterId || tbdOnly || eventStatusFilter || visibilityFilter ? `No events match your search criteria.` : "No events available."}
            </p>
            {(headerSearch || searchEventName || dateFrom || dateTo || leagueFilterId || tbdOnly || eventStatusFilter || visibilityFilter) && (
              <button onClick={handleClearAllSearches} className={styles.clearAllBtn}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={selectedIds.length > 0 && !isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={loading || bulkActionLoading}
                      sx={{
                        color: themeColors.textSecondary,
                        "&.Mui-checked": { color: themeColors.buttonBgPrimary },
                      }}
                    />
                  </th>
                  {renderSortHeader("EVENT NAME", "e_name")}
                  <th>DISPLAY NAME</th>
                  <th>VENUE</th>
                  {renderSortHeader("DATE", "e_date")}
                  {renderSortHeader("TIME", "e_time")}
                  {renderSortHeader("LEAGUE", "league")}
                  <th>STATUS</th>
                  {renderSortHeader("EVENT STATUS", "e_status")}
                  <th className={styles.actionCol}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event: any) => {
                  const displayTime = event.time && event.time !== "TBD" ? event.time : "TBD";
                  return (
                    <tr key={event.id}>
                      <td>
                        <Checkbox
                          checked={selectedIds.includes(event.id)}
                          onChange={(e) => handleSelectOne(event.id, e.target.checked)}
                          disabled={loading || bulkActionLoading}
                          sx={{
                            color: themeColors.textSecondary,
                            "&.Mui-checked": { color: themeColors.buttonBgPrimary },
                          }}
                        />
                      </td>
                      <td className={styles.eventName}>{event.eventName}</td>
                      <td>{event.displayName}</td>
                      <td className={styles.venue}>{event.venue}</td>
                      <td className={styles.date}>{event.date}</td>
                      <td className={styles.time}>{displayTime}</td>
                      <td className={styles.league}>
                        <span className={`${styles.leagueBadge} ${getLeagueBadgeClass(event.league || "")}`}>
                          {event.league || "Unknown"}
                        </span>
                      </td>
                      <td>
                        {event.status ? (
                          <span className={styles.statusActive}>Show</span>
                        ) : (
                          <span className={styles.statusInactive}>Hidden</span>
                        )}
                      </td>
                      <td>
                     {renderEventStatusBadge(String(event.rawStatus || "").toLowerCase()) || (
  <span className={`${styles.eventStatusBadgeText}`}>
    unknown
  </span>
)}
                      </td>
                      <td className={styles.actionCol}>
                        <IconButton
                          size="small"
                          onClick={(menuEvent) => {
                            handleActionMenuOpen(menuEvent.currentTarget, event);
                          }}
                          sx={{ color: themeColors.textSecondary }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className={styles.tableFooter}>
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            Showing {sortedEvents.length} of {totalCount} events
          </div>
          <div className={styles.paginationControls}>
            <span className={styles.paginationInfo}>
              Page {currentPage} of {Math.max(1, totalPages)}
            </span>
            <button
              onClick={() => handlePageChange(null, currentPage - 1)}
              disabled={currentPage === 1 || loading || sortedEvents.length === 0}
              className={styles.paginationBtn}
            >
              ← Previous
            </button>
            <button
              onClick={() => {
                const maxPage = Math.ceil(totalCount / rowsPerPage);
                if (currentPage < maxPage) {
                  handlePageChange(null, currentPage + 1);
                }
              }}
              disabled={currentPage >= totalPages || loading || sortedEvents.length === 0}
              className={styles.paginationBtn}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
      >
        {(() => {
          const actionEvent = transformedEvents.find((item: any) => item.id === actionsEventId) || closeEventTarget;
          if (!actionEvent) return null;
          const isEventClosed = String(actionEvent.rawStatus || "").trim().toLowerCase() === "closed";
          return (
            <>
              <MenuItem onClick={() => { closeActionsMenu(); handleEdit(actionEvent); }}>
                Edit
              </MenuItem>
              {actionEvent.status ? (
                <MenuItem onClick={() => { closeActionsMenu(); handleHide(actionEvent.id); }}>
                  Hide
                </MenuItem>
              ) : (
                <MenuItem onClick={() => { closeActionsMenu(); handleShow(actionEvent.id); }}>
                  Unhide
                </MenuItem>
              )}
              {!isEventClosed && (
                <MenuItem onClick={() => { openCloseConfirmDialog(); }}>
                  Close Event
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      <Dialog
        open={closeConfirmOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setCloseConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Close Event?</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to close this event?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)} disabled={closeEventLoading}>Cancel</Button>
          <Button onClick={handleConfirmCloseEvent} disabled={closeEventLoading} variant="contained" color="error">
            {closeEventLoading ? "Closing..." : "Close Event"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddModal}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: darkMode ? "0 20px 25px -5px rgba(0, 0, 0, 0.5)" : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            transition: "all 0.3s ease",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderBottom: `1px solid ${themeColors.borderColor}`,
            pb: 2,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            flexShrink: 0,
            margin: 0,
            transition: "all 0.3s ease",
          }}
        >
          Add New Event
          <IconButton
            onClick={handleCloseModal}
            sx={{ ml: "auto", p: 0.5, color: themeColors.textSecondary }}
            disabled={addLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            pt: 2.5,
            overflowY: "auto",
            flex: 1,
            backgroundColor: themeColors.bgPrimary,
            transition: "all 0.3s ease",
          }}
        >
          <p
            style={{
              color: themeColors.textSecondary,
              fontSize: "13px",
              margin: "0 0 8px 0",
              fontWeight: 500,
              transition: "color 0.3s ease",
            }}
          >
            Enter the event details below. All fields marked <span style={{ color: "#dc2626", fontWeight: 600 }}>*</span>{" "}
            are required.
          </p>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: themeColors.textLabel,
                marginBottom: "6px",
                transition: "color 0.3s ease",
              }}
            >
              Event Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              min={todayDate}
              type="text"
              value={formData.eventName}
              onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
              placeholder="e.g., Sugar Bowl — Texas vs Ohio State"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${themeColors.borderInput}`,
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "inherit",
                boxSizing: "border-box",
                backgroundColor: themeColors.bgSecondary,
                color: themeColors.textPrimary,
                transition: "all 0.3s ease",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: themeColors.textLabel,
                marginBottom: "6px",
                transition: "color 0.3s ease",
              }}
            >
              Display Name (optional - for team announcements)
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Sugar Bowl — Texas vs Ohio State"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${themeColors.borderInput}`,
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "inherit",
                boxSizing: "border-box",
                backgroundColor: themeColors.bgSecondary,
                color: themeColors.textPrimary,
                transition: "all 0.3s ease",
              }}
            />
            <p
              style={{
                color: themeColors.infoText,
                fontSize: "12px",
                margin: "6px 0 0 0",
                fontWeight: 400,
                transition: "color 0.3s ease",
              }}
            >
              Leave blank to use Event Name initially. Update later when teams are announced.
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: themeColors.textLabel,
                marginBottom: "6px",
                transition: "color 0.3s ease",
              }}
            >
              Event Date <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                min={todayDate}
                value={formData.eventDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    eventDate: e.target.value,
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  paddingRight: "36px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease",
                }}
              />
            </div>
          </div>

          <div className={styles.eventTimeLeagueRow}>
            <div className={styles.eventField}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: themeColors.textLabel,
                  marginBottom: "6px",
                  transition: "color 0.3s ease",
                }}
              >
                City <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="e.g., New Orleans"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: themeColors.textLabel,
                  marginBottom: "6px",
                  transition: "color 0.3s ease",
                }}
              >
                State <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="e.g., LA"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  transition: "all 0.3s ease",
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: themeColors.textLabel,
                marginBottom: "6px",
                transition: "color 0.3s ease",
              }}
            >
              Venue Name (searchable) <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <Autocomplete
              freeSolo
              options={(venuesData?.events || []).map((v: any) => v.e_address).filter(Boolean)}
              loading={venuesLoading}
              value={formData.venue}
              onInputChange={(_e, value) => {
                setFormData({ ...formData, venue: value });
                if (value && value.length >= 2) {
                  searchVenues({ variables: { searchText: `%${value}%` } });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Venue Name (searchable)"
                  placeholder="Search or type venue name..."
                  required
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: themeColors.textLabel,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: themeColors.buttonBgPrimary,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: themeColors.bgSecondary,
                      "& fieldset": {
                        borderColor: themeColors.borderInput,
                      },
                      "&:hover fieldset": {
                        borderColor: themeColors.borderInput,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: themeColors.buttonBgPrimary,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: themeColors.textPrimary,
                    },
                    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
                      color: themeColors.textSecondary,
                    },
                  }}
                />
              )}
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: themeColors.textLabel,
                  marginBottom: "6px",
                  transition: "color 0.3s ease",
                }}
              >
                Event Time <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                className={styles.eventTimeInput}
                type="time"
                min={formData.eventDate === todayDate ? currentTime : undefined}
                value={formData.eventTime}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    eventTime: e.target.value,
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${themeColors.borderInput}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  backgroundColor: themeColors.bgSecondary,
                  color: themeColors.textPrimary,
                  minHeight: "42px",
                }}
              />
            </div>
            <div className={styles.eventField}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: themeColors.textLabel,
                  marginBottom: "6px",
                  transition: "color 0.3s ease",
                }}
              >
                League <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <Autocomplete
                className={styles.leagueAutocomplete}
                disablePortal
                options={leaguesData?.leagues || []}
                loading={leaguesLoading}
                value={
                  leaguesData?.leagues?.find((league: any) => league.l_name === formData.league) || null
                }
                onChange={(_event, newValue: any) => {
                  setFormData({
                    ...formData,
                    league: newValue?.l_name || "",
                    homeTeam: "",
                    awayTeam: "",
                  });
                }}
                getOptionLabel={(option: any) => option?.l_name || ""}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  if (!query) return options;

                  return options.filter((option: any) =>
                    (option?.l_name || "").toLowerCase().includes(query)
                  );
                }}
                isOptionEqualToValue={(option: any, value: any) => option?.l_id === value?.l_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={leaguesLoading ? "Loading leagues..." : "Search league..."}
                    sx={{
                      width: "100%",
                      backgroundColor: themeColors.bgSecondary,
                      '& .MuiOutlinedInput-root': {
                        minHeight: '42px',
                        borderRadius: '8px',
                        borderColor: themeColors.borderInput,
                        '& fieldset': {
                          borderColor: themeColors.borderInput,
                        },
                        '&:hover fieldset': {
                          borderColor: themeColors.borderInput,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: themeColors.buttonBgPrimary,
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: themeColors.textPrimary,
                        fontSize: '13px',
                        padding: '10px 12px',
                      },
                      '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                        color: themeColors.textSecondary,
                      },
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: themeColors.textLabel,
                  marginBottom: "6px",
                  transition: "color 0.3s ease",
                }}
              >
                Timezone
              </label>
              <Autocomplete
                options={timezoneOptions}
                value={timezoneOptions.find((option) => option.value === formData.timezone) ?? null}
                onChange={(_event, newValue) => {
                  setFormData({
                    ...formData,
                    timezone: newValue?.value || "",
                  });
                }}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  if (!query) return options;

                  return options.filter((option) => {
                    const text = `${option.key} ${option.value}`.toLowerCase();
                    return text.includes(query);
                  });
                }}
                getOptionLabel={(option) => option.key}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={timezoneLoading ? "Loading timezones..." : "Search timezone..."}
                    disabled={timezoneLoading}
                    sx={{
                      width: "100%",
                      backgroundColor: themeColors.bgSecondary,
                      '& .MuiOutlinedInput-root': {
                        minHeight: '42px',
                        borderRadius: '8px',
                        borderColor: themeColors.borderInput,
                        '& fieldset': {
                          borderColor: themeColors.borderInput,
                        },
                        '&:hover fieldset': {
                          borderColor: themeColors.borderInput,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: themeColors.buttonBgPrimary,
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: themeColors.textPrimary,
                        fontSize: '13px',
                        padding: '10px 12px',
                      },
                    }}
                  />
                )}
              />
            </div>
          </div>

          {!shouldHideTeamSection(formData.league) && (
            <div style={{ display: "flex", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: themeColors.textLabel,
                    marginBottom: "6px",
                    transition: "color 0.3s ease",
                  }}
                >
                  Home Team (optional)
                </label>
                <Autocomplete
                  freeSolo
                  options={selectedLeagueTeamOptions}
                  value={formData.homeTeam}
                  onInputChange={(_event, value) =>
                    setFormData({ ...formData, homeTeam: value })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedLeague ? "Select home team" : "Select league first"}
                      sx={{
                        width: "100%",
                        backgroundColor: themeColors.bgSecondary,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          borderColor: themeColors.borderInput,
                        },
                        '& .MuiInputBase-input': {
                          color: themeColors.textPrimary,
                        },
                      }}
                    />
                  )}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: themeColors.textLabel,
                    marginBottom: "6px",
                    transition: "color 0.3s ease",
                  }}
                >
                  Away Team (optional)
                </label>
                <Autocomplete
                  freeSolo
                  options={selectedLeagueTeamOptions}
                  value={formData.awayTeam}
                  onInputChange={(event, value) =>
                    setFormData({ ...formData, awayTeam: value })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedLeague ? "Select away team" : "Select league first"}
                      sx={{
                        width: "100%",
                        backgroundColor: themeColors.bgSecondary,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          borderColor: themeColors.borderInput,
                        },
                        '& .MuiInputBase-input': {
                          color: themeColors.textPrimary,
                        },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px",
            gap: "10px",
            borderTop: `1px solid ${themeColors.borderColor}`,
            position: "sticky",
            bottom: 0,
            backgroundColor: themeColors.bgPrimary,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleCloseModal}
            disabled={addLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: themeColors.textSecondary,
              border: `1px solid ${themeColors.borderInput}`,
              "&:hover": {
                backgroundColor: themeColors.bgHover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddEvent}
            disabled={addLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: themeColors.buttonBgPrimary,
              color: themeColors.buttonTextPrimary,
              minWidth: "100px",
              "&:hover": {
                backgroundColor: themeColors.buttonBgHover,
              },
            }}
          >
            {addLoading ? <CircularProgress size={20} sx={{ color: themeColors.buttonTextPrimary }} /> : "Add Event"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditModal}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setOpenEditModal(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: darkMode ? "0 20px 25px -5px rgba(0, 0, 0, 0.5)" : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            transition: "all 0.3s ease",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${themeColors.borderColor}`,
            pb: 2,
            backgroundColor: themeColors.bgPrimary,
            color: themeColors.textPrimary,
            transition: "all 0.3s ease",
          }}
        >
          Edit Event Date & Time
          <IconButton
            onClick={() => setOpenEditModal(false)}
            disabled={editLoading}
            sx={{ color: themeColors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            pt: 2.5,
            backgroundColor: themeColors.bgPrimary,
            transition: "all 0.3s ease",
          }}
        >
          {selectedEvent && (
            <div
              style={{
                backgroundColor: themeColors.bgInfoLight,
                padding: "14px",
                borderRadius: "10px",
                border: `1px solid ${themeColors.borderColor}`,
                transition: "all 0.3s ease",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: themeColors.textPrimary,
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                {selectedEvent.eventName}
              </h3>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "13px",
                  color: themeColors.textSecondary,
                }}
              >
                {selectedEvent.venue}
              </p>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "12px",
                  color: themeColors.textTertiary,
                }}
              >
                Current Date: {selectedEvent.date} | Current Time: {selectedEvent.time}
              </p>
            </div>
          )}

          <TextField
            label="Display Name (shown in app)"
            value={editFormData.displayName}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                displayName: e.target.value,
              })
            }
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: themeColors.textLabel,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: themeColors.buttonBgPrimary,
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: themeColors.bgSecondary,
                "& fieldset": {
                  borderColor: themeColors.borderInput,
                },
                "&:hover fieldset": {
                  borderColor: themeColors.borderInput,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.buttonBgPrimary,
                },
              },
              "& .MuiInputBase-input": {
                color: themeColors.textPrimary,
              },
            }}
          />

          <div style={{ display: "flex", gap: "12px" }}>
            <TextField
              label="New Event Date"
              type="date"
              value={editFormData.eventDate}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  eventDate: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: todayDate,
              }}
              fullWidth
              sx={{
                "& .MuiInputLabel-root": {
                  color: themeColors.textLabel,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: themeColors.buttonBgPrimary,
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: themeColors.bgSecondary,
                  "& fieldset": {
                    borderColor: themeColors.borderInput,
                  },
                  "&:hover fieldset": {
                    borderColor: themeColors.borderInput,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.buttonBgPrimary,
                  },
                },
                "& .MuiInputBase-input": {
                  color: themeColors.textPrimary,
                },
              }}
            />

            <TextField
              label="New Event Time"
              type="time"
              value={editFormData.eventTime}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  eventTime: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: editFormData.eventDate === todayDate ? currentTime : undefined,
              }}
              fullWidth
              sx={{
                "& .MuiInputLabel-root": {
                  color: themeColors.textLabel,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: themeColors.buttonBgPrimary,
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: themeColors.bgSecondary,
                  "& fieldset": {
                    borderColor: themeColors.borderInput,
                  },
                  "&:hover fieldset": {
                    borderColor: themeColors.borderInput,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.buttonBgPrimary,
                  },
                },
                "& .MuiInputBase-input": {
                  color: themeColors.textPrimary,
                },
              }}
            />
          </div>

          <div
            style={{
              backgroundColor: themeColors.bgInfoLight,
              padding: "12px",
              borderRadius: "8px",
              fontSize: "13px",
              color: themeColors.infoBoxText,
              border: `1px solid ${themeColors.borderColor}`,
              transition: "all 0.3s ease",
            }}
          >
            <strong>Note:</strong> Updated date/time will reflect everywhere this event appears in the dashboard,
            including all associated tickets.
          </div>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px",
            gap: "10px",
            borderTop: `1px solid ${themeColors.borderColor}`,
            backgroundColor: themeColors.bgPrimary,
            transition: "all 0.3s ease",
          }}
        >
          <Button
            onClick={() => setOpenEditModal(false)}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: themeColors.textSecondary,
              border: `1px solid ${themeColors.borderInput}`,
              "&:hover": {
                backgroundColor: themeColors.bgHover,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editLoading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: themeColors.buttonBgPrimary,
              color: themeColors.buttonTextPrimary,
              minWidth: "100px",
              "&:hover": {
                backgroundColor: themeColors.buttonBgHover,
              },
            }}
          >
            {editLoading ? <CircularProgress size={18} sx={{ color: themeColors.buttonTextPrimary }} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EventsUpdate;